'use client';

/** Circular icon button for dismissing modals, panels and drawers. Sizes: "md" (default, 44px touch target) and "sm" (28px, inline contexts). */

import * as React from 'react';
import { disabledTokens, focus as focusTokens } from '@federa/utils/styles/interaction-tokens';
import { DEFAULT_CLOSE_LABEL } from '@federa/const/aria-default-labels';
import { FLEX_CENTER_STYLE } from '@federa/const/style-flex-presets';
import { FOCUS_VISIBLE_SELECTOR } from '@federa/const/focus-visible';
import { GHOST_BUTTON_BASE } from '../common/layout-styles.ts';
import { BUTTON_TYPE } from '../common/aria-tokens.ts';
import { colors } from '../css-refs.colors.ts';
import { spacing } from '../css-refs.spacing.ts';
import { borderRadius, motion } from '../css-refs.effects.ts';
import { touchTargets } from '../css-refs.layout.ts';
import { SemanticIcon } from '../icons/SemanticIcon.tsx';
import type { CloseButtonProps, CloseButtonSize } from '@federa/types/ui-button';
import type { IconSize } from '@federa/types/ui-icons';

// `IconSize`, not a re-spelling of two of its members: this field is handed straight to `SemanticIcon`'s `size` prop,
// so the icon vocabulary IS its type, and an inline `'sm' | 'md'` was a second copy of a set that leaf already owns.
const sizeConfig: Record<CloseButtonSize, { dimension: string; iconSize: IconSize }> = {
	sm: { dimension: spacing[7], iconSize: 'sm' },
	md: { dimension: touchTargets.minimum, iconSize: 'sm' },
};

/** The four booleans that decide what this button looks like. Exported with the resolver below so the decision is testable without a DOM. */
export interface CloseButtonVisualState {
	size: CloseButtonSize;
	hovered: boolean;
	focused: boolean;
	disabled: boolean;
}

function getBaseStyle(size: CloseButtonSize): React.CSSProperties {
	const config = sizeConfig[size];
	return {
		...FLEX_CENTER_STYLE,
		width: config.dimension,
		height: config.dimension,
		padding: 0,
		...GHOST_BUTTON_BASE,
		borderRadius: borderRadius.full,
		color: colors.text.tertiary,
		transition: `background-color ${motion.duration.fast} ${motion.easing.standard}, color ${motion.duration.fast} ${motion.easing.standard}`,
		flexShrink: 0,
	};
}

/** Hover repaint as DATA, replacing the two handlers that reached into `e.currentTarget.style` to set it. */
function getHoverStyle(): React.CSSProperties {
	return { backgroundColor: colors.background.tertiary, color: colors.text.secondary };
}

/**
 * The token focus ring, the same one every other control in the design system draws (`focus.ring.default`,
 * `utils/styles/interaction-tokens`). This button composed NONE of it -- its style bag was identical focused and
 * resting -- so the dismiss control of a modal fell back to whatever rectangular outline the browser chose to draw over
 * a `border-radius: 50%` element, on the one control that closes the dialog. `outline: none` stands the mismatched UA
 * ring down in favour of the shaped one, which is only safe BECAUSE the box-shadow replaces it.
 */
function getFocusStyle(): React.CSSProperties {
	return { boxShadow: focusTokens.ring.default.boxShadow, outline: 'none' };
}

/**
 * The WHOLE visual decision for this button, as one pure function of its state -- which is what makes the focus ring
 * assertable at all. It replaces two `onMouseEnter`/`onMouseLeave` handlers that wrote
 * `e.currentTarget.style.backgroundColor` directly: a second styling channel React does not know about, so it survived
 * re-renders that should have cleared it, silently overwrote whatever the `style` prop set for the same property, and
 * could not be read back by any test.
 *
 * A DISABLED CONTROL SHOWS NEITHER STATE. It is out of the tab order and ignores the pointer, so a hover repaint or a
 * focus ring on it would be a promise the control cannot keep.
 */
export function getCloseButtonStyles(state: CloseButtonVisualState, override?: React.CSSProperties): React.CSSProperties {
	const interactive = !state.disabled;
	return {
		...getBaseStyle(state.size),
		...override,
		...(interactive && state.hovered && getHoverStyle()),
		...(interactive && state.focused && getFocusStyle()),
		...(state.disabled && { opacity: disabledTokens.opacity, cursor: disabledTokens.cursor }),
	};
}

export function CloseButton({
	onClick,
	'aria-label': ariaLabel = DEFAULT_CLOSE_LABEL,
	size = 'md',
	className,
	style,
	disabled = false,
}: CloseButtonProps) {
	const config = sizeConfig[size];
	// STATE, NOT DIRECT DOM MUTATION -- see `getCloseButtonStyles`, which now owns the whole decision.
	const [isHovered, setIsHovered] = React.useState(false);
	const [isFocused, setIsFocused] = React.useState(false);

	return (
		<button
			type={BUTTON_TYPE.button}
			onClick={onClick}
			className={className}
			style={getCloseButtonStyles({ size, hovered: isHovered, focused: isFocused, disabled }, style)}
			onMouseEnter={disabled ? undefined : () => setIsHovered(true)}
			onMouseLeave={disabled ? undefined : () => setIsHovered(false)}
			// `:focus-visible`, NOT `:focus` -- the ring is for keyboard users and was painting on every TAP as well.
			// A pointer press focuses the button, so on touch the brand-coloured ring latched around the dismiss control
			// and stayed there after the finger lifted, which read as a stuck selected state rather than as focus. The
			// platform already draws this distinction; matching it here keeps the ring for keyboard navigation, which is
			// the reason it was added, while a tap now leaves the control unpainted.
			onFocus={disabled ? undefined : (event) => setIsFocused(event.currentTarget.matches(FOCUS_VISIBLE_SELECTOR))}
			onBlur={disabled ? undefined : () => setIsFocused(false)}
			aria-label={ariaLabel}
			disabled={disabled}
		>
			<SemanticIcon name="close" size={config.iconSize} />
		</button>
	);
}
